import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const InsightsPanel = ({ insights }) => {
  const [selectedInsight, setSelectedInsight] = useState(null);

  const getInsightIcon = (type) => {
    switch (type) {
      case 'opportunity': return 'Target';
      case 'warning': return 'AlertTriangle';
      case 'success': return 'TrendingUp';
      case 'optimization': return 'Zap';
      default: return 'Info';
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'opportunity': return 'text-primary';
      case 'warning': return 'text-warning';
      case 'success': return 'text-success';
      case 'optimization': return 'text-accent';
      default: return 'text-muted-foreground';
    }
  };

  const getInsightBg = (type) => {
    switch (type) {
      case 'opportunity': return 'bg-primary/10';
      case 'warning': return 'bg-warning/10';
      case 'success': return 'bg-success/10';
      case 'optimization': return 'bg-accent/10';
      default: return 'bg-muted/10';
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      high: 'bg-error text-error-foreground',
      medium: 'bg-warning text-warning-foreground',
      low: 'bg-muted text-muted-foreground'
    };
    return colors?.[priority] || colors?.low;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 elevation-2">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Automated Insights</h3>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
          <span className="text-sm text-muted-foreground">AI-Powered</span>
        </div>
      </div>
      <div className="space-y-4">
        {insights?.map((insight) => (
          <div 
            key={insight?.id} 
            className={`p-4 rounded-lg border-l-4 cursor-pointer transition-smooth hover:elevation-2 ${getInsightBg(insight?.type)}`}
            style={{ borderLeftColor: `var(--color-${insight?.type === 'opportunity' ? 'primary' : insight?.type === 'warning' ? 'warning' : insight?.type === 'success' ? 'success' : 'accent'})` }}
            onClick={() => setSelectedInsight(selectedInsight === insight?.id ? null : insight?.id)}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <Icon 
                  name={getInsightIcon(insight?.type)} 
                  size={20} 
                  className={getInsightColor(insight?.type)} 
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <h4 className="text-sm font-medium text-foreground">{insight?.title}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityBadge(insight?.priority)}`}>
                      {insight?.priority}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{insight?.description}</p>
                  
                  {selectedInsight === insight?.id && (
                    <div className="mt-4 space-y-3">
                      <div className="bg-background/50 rounded-lg p-3">
                        <h5 className="text-sm font-medium text-foreground mb-2">Detailed Analysis</h5>
                        <p className="text-sm text-muted-foreground">{insight?.analysis}</p>
                      </div>
                      
                      {insight?.recommendations && (
                        <div className="bg-background/50 rounded-lg p-3">
                          <h5 className="text-sm font-medium text-foreground mb-2">Recommendations</h5>
                          <ul className="space-y-1">
                            {insight?.recommendations?.map((rec, index) => (
                              <li key={index} className="text-sm text-muted-foreground flex items-start space-x-2">
                                <Icon name="ArrowRight" size={14} className="mt-0.5 text-primary" />
                                <span>{rec}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {insight?.actions && (
                        <div className="flex items-center space-x-2 pt-2">
                          {insight?.actions?.map((action, index) => (
                            <button 
                              key={index}
                              className="px-3 py-1 bg-primary text-primary-foreground rounded-md text-xs font-medium hover:bg-primary/90 transition-smooth"
                            >
                              {action}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-2 ml-4">
                <span className="text-xs text-muted-foreground">{insight?.timestamp}</span>
                <Icon 
                  name={selectedInsight === insight?.id ? "ChevronUp" : "ChevronDown"} 
                  size={16} 
                  className="text-muted-foreground" 
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Brain" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Insights Generated Today</span>
          </div>
          <span className="text-lg font-bold text-primary">{insights?.length}</span>
        </div>
      </div>
    </div>
  );
};

export default InsightsPanel;